import { isArray, isNull, isPlainObject, transform } from 'lodash-es'

/**
 * Checks if a value is null or undefined.
 *
 * @param value The value to check.
 * @returns True if the value is null or undefined, false otherwise.
 */
const isNullOrUndefined = (value: unknown): value is null | undefined => {
  return value === null || value === undefined
}

/**
 * Deeply removes null values from an object or array.
 * - Removes properties from objects if their value is null.
 * - Removes elements from arrays if the element itself is null.
 * - Recursively cleans nested objects and arrays.
 * - Does not mutate the original object/array.
 *
 * @param value The object or array to clean.
 * @returns A new object or array with nulls removed, or the original value if not an object/array.
 */
const deepCleanNulls = (value: unknown): unknown => {
  // If the value is an array, map over its elements,
  // recursively calling deepCleanNulls on each.
  // Then, filter out any elements that are strictly null after cleaning.
  if (isArray(value)) {
    // Replace chain with direct array methods
    return value.map((item: unknown) => deepCleanNulls(item)).filter((item: unknown) => !isNull(item))
  }

  // If the value is a plain object, transform it.
  // For each key-value pair, recursively call deepCleanNulls on the value.
  // If the cleaned value is not null, assign it to the result.
  if (isPlainObject(value)) {
    // lodash's isPlainObject() doesn't narrow the type, so we assert the shape it confirms at runtime.
    return transform(
      value as Record<string, unknown>,
      (result: Record<string, unknown>, val: unknown, key: string) => {
        const cleanedValue = deepCleanNulls(val) // Recursive call
        if (!isNull(cleanedValue)) {
          result[key] = cleanedValue
        }
      },
      {} as Record<string, unknown> // Initialize with an empty object
    )
  }

  // For primitives (string, number, boolean, undefined, symbol), Date, RegExp, Function, etc.,
  // return the value as is. If the input 'value' itself is null, it will be returned as null,
  // and the calling context (the array filter or object transform) will handle it.
  return value
}

export { isNullOrUndefined, deepCleanNulls }
